import React, { useRef, useState, useEffect } from 'react';
import {
  EditorState,
  convertToRaw,
  ContentState,
  convertFromHTML,
} from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import './react-draft-wysiwyg.css';

const HTML_ENTITY_PATTERN = /&(lt|gt|amp|quot|apos|nbsp|#39|#[0-9]+|#x[0-9a-f]+);/i;
const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i;
const HTML_BLOCK_TAG_PATTERN = /<(address|article|aside|blockquote|div|dl|fieldset|figcaption|figure|footer|form|h[1-6]|header|hr|li|main|nav|ol|p|pre|section|table|ul)(\s|>|\/)/i;
const BR_TAG_PATTERN = /<br\s*\/?\s*>/i;
const P_OR_DIV_BLOCK_PATTERN = /<(p|div)([^>]*)>([\s\S]*?)<\/\1>/gi;

const normaliseHtmlValue = (value) => (typeof value === 'string' ? value : '');

const decodeHtmlEntities = (value) => {
  if (!HTML_ENTITY_PATTERN.test(value)) return value;

  if (typeof document !== 'undefined') {
    const element = document.createElement('textarea');
    element.innerHTML = value;
    return element.value;
  }

  return value
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&');
};

const hasHtmlMarkup = (value) => HTML_TAG_PATTERN.test(value);

const stripHtmlPreservingLines = (value) => {
  if (!value) return '';

  const lineAwareHtml = value
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .replace(/<\/p\s*>/gi, '\n')
    .replace(/<\/div\s*>/gi, '\n')
    .replace(/<\/h[1-6]\s*>/gi, '\n')
    .replace(/<\/li\s*>/gi, '\n')
    .replace(/<\/tr\s*>/gi, '\n')
    .replace(/<li\b[^>]*>/gi, '• ');

  let text;
  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(lineAwareHtml, 'text/html');
    text = doc.body.textContent || '';
  } else {
    text = lineAwareHtml.replace(/<[^>]*>/g, '');
  }

  return text
    .replace(/\u00a0/g, ' ')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trimEnd();
};

const createEditorStateFromPlainText = (value) => {
  if (!value.trim()) {
    return EditorState.createEmpty();
  }

  return EditorState.createWithContent(ContentState.createFromText(value));
};

const wrapHtmlFragment = (value) => {
  if (HTML_BLOCK_TAG_PATTERN.test(value)) return value;

  return `<p>${value.replace(/\r\n|\r|\n/g, '<br />')}</p>`;
};

const splitSoftBreakParagraphs = (htmlValue) => {
  return htmlValue.replace(
    P_OR_DIV_BLOCK_PATTERN,
    (match, tagName, attributes, innerHtml) => {
      if (!BR_TAG_PATTERN.test(innerHtml)) return match;

      return innerHtml
        .split(/<br\s*\/?\s*>/gi)
        .map((part) => `<${tagName}${attributes}>${part || '<br />'}</${tagName}>`)
        .join('');
    },
  );
};

const createContentStateWithDraftJs = (htmlValue) => {
  const converted = convertFromHTML(htmlValue);

  if (!converted?.contentBlocks?.length) return null;

  return ContentState.createFromBlockArray(
    converted.contentBlocks,
    converted.entityMap,
  );
};

const createContentStateWithHtmlToDraft = (htmlValue) => {
  const contentBlock = htmlToDraft(htmlValue);

  if (!contentBlock?.contentBlocks?.length) return null;

  return ContentState.createFromBlockArray(
    contentBlock.contentBlocks,
    contentBlock.entityMap,
  );
};

const safelyCreateContentState = (converter, htmlValue) => {
  try {
    return converter(htmlValue);
  } catch (err) {
    console.warn('Error converting HTML to Draft:', err);
    return null;
  }
};

const createEditorStateFromHtml = (value) => {
  const rawValue = normaliseHtmlValue(value);
  const decodedValue = decodeHtmlEntities(rawValue);

  if (!decodedValue.trim()) {
    return EditorState.createEmpty();
  }

  if (!hasHtmlMarkup(decodedValue)) {
    return createEditorStateFromPlainText(decodedValue);
  }

  const htmlValue = splitSoftBreakParagraphs(wrapHtmlFragment(decodedValue));
  const contentState = safelyCreateContentState(createContentStateWithDraftJs, htmlValue)
    || safelyCreateContentState(createContentStateWithHtmlToDraft, htmlValue);

  if (contentState) {
    return EditorState.createWithContent(contentState);
  }

  return createEditorStateFromPlainText(stripHtmlPreservingLines(decodedValue));
};

const DraftEditor = ({
  value,
  keyValue,
  onCommit,
}) => {
  const [editorState, setEditorState] = useState(() => createEditorStateFromHtml(value));
  const [comment, setComment] = useState(normaliseHtmlValue(value));

  const inputRef = useRef(null);
  const latestComment = useRef(comment);
  const initialRawContent = useRef(
    JSON.stringify(convertToRaw(editorState.getCurrentContent())),
  );
  const latestRawContent = useRef(initialRawContent.current);
  const dirtyRef = useRef(false);
  const onCommitRef = useRef(onCommit);

  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  useEffect(() => {
    // A parent/grid re-render must not wipe text that is currently being
    // edited. Only accept a new external value while the editor is clean.
    if (dirtyRef.current) return;

    const nextComment = normaliseHtmlValue(value);
    const nextEditorState = createEditorStateFromHtml(nextComment);
    const nextRawContent = JSON.stringify(
      convertToRaw(nextEditorState.getCurrentContent()),
    );

    latestComment.current = nextComment;
    initialRawContent.current = nextRawContent;
    latestRawContent.current = nextRawContent;
    dirtyRef.current = false;
    setComment(nextComment);
    setEditorState(nextEditorState);
  }, [value]);

  useEffect(() => {
    latestComment.current = comment;
  }, [comment]);

  const commitCurrentComment = () => {
    const committedComment = latestComment.current;

    if (!dirtyRef.current) {
      return;
    }

    dirtyRef.current = false;
    initialRawContent.current = latestRawContent.current;
    onCommitRef.current(committedComment);
  };

  useEffect(() => {
    return () => {
      // react-data-grid unmounts an edit cell when the user leaves it.
      // Commit real edits only. Merely opening a cell must never save an
      // empty value or trigger a refresh that closes the editor.
      if (!dirtyRef.current) return;

      const committedComment = latestComment.current;

      setTimeout(() => {
        onCommitRef.current(committedComment);
      }, 0);
    };
  }, []);

  const onEditorStateChange = (newEditorState) => {
    const nextRaw = convertToRaw(newEditorState.getCurrentContent());
    const nextRawContent = JSON.stringify(nextRaw);
    const nextComment = draftToHtml(nextRaw);

    latestRawContent.current = nextRawContent;
    dirtyRef.current = nextRawContent !== initialRawContent.current;
    latestComment.current = nextComment;
    setEditorState(newEditorState);
    setComment(nextComment);
  };

  const BlockKeys = ({ children, onEscape }) => {
    const handleKeyDown = (ev) => {
      if (ev.keyCode === 27) {
        ev.stopPropagation();
        onEscape();
      }
      if ([9, 13, 27, 37, 38, 39, 40].includes(ev.keyCode)) {
        ev.stopPropagation();
      }
    };
    return <div onKeyDown={handleKeyDown}>{children}</div>;
  };

  return (
    <div
      ref={inputRef}
      onMouseDown={(ev) => ev.stopPropagation()}
      onClick={(ev) => ev.stopPropagation()}
      onDoubleClick={(ev) => ev.stopPropagation()}
      style={{
        border: '1px solid gray',
        background: '#fff',
        minHeight: 260,
        width: 542,
        marginTop: -50,
        zIndex: 2,
        marginLeft: keyValue === 'Comment' ? -365 : 0,
        position: 'fixed',
      }}
    >
      <BlockKeys onEscape={commitCurrentComment}>
        <Editor
          wrapperClassName="wrapper-class"
          editorClassName="editor-class"
          toolbarClassName="toolbar-class"
          editorState={editorState}
          onEditorStateChange={onEditorStateChange}
          handlePastedText={() => false}
        />
      </BlockKeys>
    </div>
  );
};

export default DraftEditor;
